import { createContext, useContext, ReactNode, useEffect, useState } from "react";
import { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import { persistentLogger } from "@/lib/persistent-logger";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = async () => {
    try {
      const { data: { user: currentUser }, error } = await supabase.auth.getUser();
      
      if (error) {
        logger.error('Failed to fetch user in AuthContext', error);
        persistentLogger.addLog('AUTH', 'error', 'Failed to fetch user', { error: String(error) });
        setUser(null);
        return;
      }
      
      setUser(currentUser);
      logger.info('User loaded in AuthContext', { userId: currentUser?.id });
      persistentLogger.addLog('AUTH', 'info', 'User authenticated', { 
        userId: currentUser?.id,
        email: currentUser?.email 
      });
    } catch (error) {
      logger.error('Error fetching user in AuthContext', error);
      persistentLogger.addLog('AUTH', 'error', 'Exception fetching user', { error: String(error) });
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const refreshUser = async () => {
    setLoading(true);
    await fetchUser();
  };

  return (
    <AuthContext.Provider value={{ user, loading, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
